/**
 * Case Re-Encryption Service
 *
 * Client-side service that re-encrypts case fields from field-level RSA (ENC:/HENC:)
 * to envelope encryption (CASEKEY:) using the per-case AES key.
 *
 * This runs when a doctor opens a case that still has old-style RSA-encrypted fields.
 * The doctor's RSA private key decrypts the old fields, then the case key re-encrypts
 * them with CASEKEY: prefix for multi-party access.
 *
 * ## Persistent "Decryption Failed" — 2026-05-14 incident
 *
 * The prior implementation silently swallowed `decryptError` and dropped the
 * field from `reEncryptedFields`. The caller had no way to know which fields
 * were left in the legacy `ENC:`/`HENC:` state, so:
 *   - The DB kept the legacy value forever
 *   - Subsequent migrations skipped the record (sessionStorage flag)
 *   - The doctor saw `[Decryption failed]` on every visit
 *
 * The functions now return BOTH `reEncryptedFields` AND `failedFields[]`, so
 * the caller can surface the failure (toast / recovery affordance) and
 * persist a per-record record of which fields need manual recovery. See
 * docs/incidents/2026-05-14-pre-screening-decryption-failed.md.
 */

import {
  encryptFieldWithCaseKey,
  isCaseKeyEncrypted,
  isFieldLevelEncrypted,
} from './caseKeyService';
import { encryptionService } from './encryptionService';

/** Result shape returned by the re-encryption helpers. */
export interface ReEncryptionResult {
  /**
   * Fields that successfully decrypted with the RSA private key and were
   * re-encrypted as `CASEKEY:`. Send these back to the backend to overwrite
   * the legacy ciphertext.
   */
  reEncryptedFields: Record<string, string>;
  /**
   * Fields that had legacy `ENC:`/`HENC:` ciphertext but FAILED to decrypt
   * with the current RSA private key. These cannot be auto-migrated; the
   * doctor needs to recover them manually (re-enter from external source,
   * or admin intervention). The UI surfaces this so the user doesn't see
   * `[Decryption failed]` forever with no path to recovery.
   */
  failedFields: string[];
}

/** Fields on a Case record that contain sensitive encrypted data */
const SENSITIVE_CASE_FIELDS = [
  'caseTitle',
  'plaintiff',
  'attorneyName',
  'attorneyEmail',
  'attorneyPhone',
  'attorneyBarNumber',
  'attorneyState',
  'firmName',
  'description',
  'documentUrl',
] as const;

/**
 * Re-encrypt all sensitive case fields from field-level RSA → envelope (case key).
 *
 * For each sensitive field:
 * - ENC:/HENC: + decrypt success → re-encrypt with case key → in reEncryptedFields
 * - ENC:/HENC: + decrypt failure → NOT in reEncryptedFields, ADDED to failedFields
 * - CASEKEY: → already migrated, skip silently
 * - plaintext → encrypt with case key → in reEncryptedFields
 * - null/undefined/empty → skip silently
 *
 * @param caseData        Raw (undecrypted) case data from API
 * @param caseKey         Unwrapped AES-256-GCM case key from envelope
 * @param privateKeyPem   Doctor's RSA-4096 private key (PEM)
 */
export async function reEncryptCaseToEnvelope(
  caseData: Record<string, any>,
  caseKey: CryptoKey,
  privateKeyPem: string,
): Promise<ReEncryptionResult> {
  const reEncryptedFields: Record<string, string> = {};
  const failedFields: string[] = [];

  for (const field of SENSITIVE_CASE_FIELDS) {
    const value = caseData[field];

    // Skip null/undefined/empty
    if (!value || typeof value !== 'string' || value.trim() === '') {
      continue;
    }

    // Already envelope-encrypted — skip
    if (isCaseKeyEncrypted(value)) {
      continue;
    }

    // Field-level RSA — decrypt then re-encrypt with case key
    if (isFieldLevelEncrypted(value)) {
      try {
        const plaintext = await encryptionService.decryptField(value, privateKeyPem);
        if (typeof plaintext === 'string') {
          reEncryptedFields[field] = await encryptFieldWithCaseKey(plaintext, caseKey);
        }
      } catch (decryptError) {
        // Don't silently drop — surface so the caller can offer recovery.
        failedFields.push(field);
        console.warn(
          `[caseReEncryption] Could not decrypt case field "${field}" with current RSA key — ` +
            'flagging for manual recovery.',
          decryptError instanceof Error ? decryptError.message : decryptError,
        );
      }
      continue;
    }

    // Plaintext — encrypt with case key
    reEncryptedFields[field] = await encryptFieldWithCaseKey(value, caseKey);
  }

  return { reEncryptedFields, failedFields };
}

/** Fields on an IntakeRequest record that contain sensitive encrypted data */
const SENSITIVE_INTAKE_REQUEST_FIELDS = [
  'plaintiff',
  'caseTitle',
  'description',
  'notes',
  'attorneyName',
  'attorneyEmail',
  'attorneyPhone',
  'firmName',
  'documentUrl',
  'appointmentType',
  'preferredDate',
  'patientPhone',
  'patientEmail',
  'patientDateOfBirth',
  'specialInstructions',
] as const;

/**
 * Re-encrypt all sensitive intake request fields from field-level RSA → envelope (case key).
 *
 * Same logic as `reEncryptCaseToEnvelope`. Returns `failedFields` for the
 * same reason — see the file-level doc for the persistence incident.
 */
export async function reEncryptIntakeRequestToEnvelope(
  data: Record<string, any>,
  caseKey: CryptoKey,
  privateKeyPem: string,
): Promise<ReEncryptionResult> {
  const reEncryptedFields: Record<string, string> = {};
  const failedFields: string[] = [];

  for (const field of SENSITIVE_INTAKE_REQUEST_FIELDS) {
    const value = data[field];

    if (!value || typeof value !== 'string' || value.trim() === '') {
      continue;
    }

    if (isCaseKeyEncrypted(value)) {
      continue;
    }

    if (isFieldLevelEncrypted(value)) {
      try {
        const plaintext = await encryptionService.decryptField(value, privateKeyPem);
        if (typeof plaintext === 'string') {
          reEncryptedFields[field] = await encryptFieldWithCaseKey(plaintext, caseKey);
        }
      } catch (decryptError) {
        failedFields.push(field);
        console.warn(
          `[caseReEncryption] Could not decrypt intake-request field "${field}" with current ` +
            'RSA key — flagging for manual recovery.',
          decryptError instanceof Error ? decryptError.message : decryptError,
        );
      }
      continue;
    }

    reEncryptedFields[field] = await encryptFieldWithCaseKey(value, caseKey);
  }

  return { reEncryptedFields, failedFields };
}

export { SENSITIVE_CASE_FIELDS, SENSITIVE_INTAKE_REQUEST_FIELDS };
